/**
 * Lightweight client-side analytics event queue.
 *
 * Events are batched in memory and flushed to `/api/public/analytics/events`
 * every 5 seconds or when the queue reaches 10 items. Failures are silently
 * dropped so analytics never block the user experience.
 */

import { apiFetch } from './api';

export interface AnalyticsEvent {
  event: string;
  ts: number;
  session_id: string;
  visitor_id: string;
  referral_code: string | null;
  payload: Record<string, unknown>;
}

const EVENT_QUEUE: AnalyticsEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
const MAX_QUEUE_SIZE = 10;
const FLUSH_INTERVAL_MS = 5000;
const SESSION_KEY = 'gs2_session_id';
const VISITOR_KEY = 'gs2_visitor_id';
const REFERRAL_KEY_PRIMARY = 'gs_referral_code';
const REFERRAL_KEY_FALLBACK = 'gs2_referral_code';

function getSessionId(): string {
  if (typeof window === 'undefined') return 'ssr';
  let sid = sessionStorage.getItem(SESSION_KEY);
  if (!sid) {
    sid = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    try {
      sessionStorage.setItem(SESSION_KEY, sid);
    } catch {
      // ignore storage errors
    }
  }
  return sid;
}

function generateVisitorId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}-${Math.random().toString(36).slice(2, 10)}`;
}

function getVisitorId(): string {
  if (typeof window === 'undefined') return 'ssr';
  let vid = localStorage.getItem(VISITOR_KEY);
  if (!vid) {
    vid = generateVisitorId();
    try {
      localStorage.setItem(VISITOR_KEY, vid);
    } catch {
      // ignore storage errors
    }
  }
  return vid;
}

function getReferralCode(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return (
      localStorage.getItem(REFERRAL_KEY_PRIMARY) ||
      localStorage.getItem(REFERRAL_KEY_FALLBACK) ||
      null
    );
  } catch {
    return null;
  }
}

function scheduleFlush(): void {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flush();
  }, FLUSH_INTERVAL_MS);
}

export async function flush(): Promise<void> {
  if (EVENT_QUEUE.length === 0) return;
  const batch = EVENT_QUEUE.splice(0, EVENT_QUEUE.length);
  try {
    await apiFetch('/api/public/analytics/events', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ events: batch }),
    });
  } catch {
    // Silently drop failed analytics to avoid breaking UX.
  }
}

function enqueue(event: string, payload: Record<string, unknown> = {}): void {
  const ev: AnalyticsEvent = {
    event,
    ts: Date.now(),
    session_id: getSessionId(),
    visitor_id: getVisitorId(),
    referral_code: getReferralCode(),
    payload,
  };
  EVENT_QUEUE.push(ev);
  if (EVENT_QUEUE.length >= MAX_QUEUE_SIZE) {
    flush();
  } else {
    scheduleFlush();
  }
}

export function trackEvent(event: string, payload: Record<string, unknown> = {}): void {
  enqueue(event, payload);
}

export function trackPageView(page: string): void {
  enqueue('page_view', {
    page,
    url: typeof window !== 'undefined' ? window.location.href : page,
  });
}

export function trackProductView(id: string, name: string): void {
  enqueue('product_view', { id, name });
}

export function trackAddToCart(id: string, qty: number, price: number): void {
  enqueue('add_to_cart', { id, qty, price });
}

export function trackBeginCheckout(cartValue: number, itemCount: number): void {
  enqueue('begin_checkout', { cartValue, itemCount });
}

export function trackOrderCreated(orderNum: string, finalPrice: number): void {
  enqueue('order_created', { orderNum, finalPrice });
}

export function trackWhatsAppClick(orderNum?: string, action?: string): void {
  enqueue('whatsapp_click', {
    orderNum: orderNum || null,
    action: action || 'contact',
  });
}

export function trackReferralLinkClick(code: string, landingPage: string): void {
  enqueue('referral_link_click', { code, landingPage });
}
